import React from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from "remotion";
import { springFade, countUp, fillBar, progressBar } from "../utils/animations";
import { crossFadeIn } from "../utils/transitions";

export interface WeekRecord {
  label: string;
  wins: number;
  losses: number;
  winRate: number; // 0–100
}

export interface AnimatedChartProps {
  title?: string;
  subtitle?: string;
  weeks: WeekRecord[];
  accentColor?: string;
  brandName?: string;
  overallWinRate?: number;
  overallRecord?: string;
}

export const AnimatedChart: React.FC<AnimatedChartProps> = ({
  title = "Season Performance",
  subtitle,
  weeks,
  accentColor = "#38bdf8",
  brandName = "POWERPICKS",
  overallWinRate,
  overallRecord,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps, durationInFrames } = useVideoConfig();
  const isVertical = height > width;

  const maxRate = Math.max(...weeks.map(w => w.winRate), 65);
  const BAR_START = 22;
  const sceneOpacity = crossFadeIn(frame, 12);

  return (
    <AbsoluteFill style={{ background: "linear-gradient(160deg, #020617 0%, #060f1e 60%, #020617 100%)", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", opacity: sceneOpacity }}>

      {/* Top accent */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accentColor, boxShadow: `0 0 20px ${accentColor}` }} />

      {/* Radial glow */}
      <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 400, background: `radial-gradient(ellipse, ${accentColor}12 0%, transparent 70%)` }} />

      <AbsoluteFill style={{ padding: isVertical ? "80px 52px 80px" : "56px 60px 60px", flexDirection: "column" }}>

        {/* Brand */}
        <div style={{ opacity: springFade(frame, fps, 0), fontSize: isVertical ? 24 : 18, color: accentColor, fontWeight: 900, letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 }}>
          {brandName}
        </div>

        {/* Title */}
        <div style={{ opacity: springFade(frame, fps, 2), fontSize: isVertical ? 54 : 38, fontWeight: 900, color: "#f1f5f9", lineHeight: 1.1, marginBottom: subtitle ? 8 : 28 }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ opacity: springFade(frame, fps, 4), fontSize: isVertical ? 30 : 22, color: "#64748b", marginBottom: 28 }}>
            {subtitle}
          </div>
        )}

        {/* Overall stat pills */}
        {(overallWinRate !== undefined || overallRecord) && (
          <div style={{ opacity: springFade(frame, fps, 7), display: "flex", gap: 16, marginBottom: 36 }}>
            {overallWinRate !== undefined && (
              <div style={{ background: `${accentColor}12`, border: `1px solid ${accentColor}33`, borderRadius: 12, padding: isVertical ? "16px 28px" : "12px 20px", textAlign: "center" }}>
                <div style={{ fontSize: isVertical ? 54 : 40, fontWeight: 900, color: accentColor, lineHeight: 1 }}>
                  {countUp(frame, 12, 40, overallWinRate)}%
                </div>
                <div style={{ fontSize: isVertical ? 20 : 15, color: "#475569", marginTop: 4 }}>Win Rate</div>
              </div>
            )}
            {overallRecord && (
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: isVertical ? "16px 28px" : "12px 20px", textAlign: "center" }}>
                <div style={{ fontSize: isVertical ? 54 : 40, fontWeight: 900, color: "#f1f5f9", lineHeight: 1 }}>{overallRecord}</div>
                <div style={{ fontSize: isVertical ? 20 : 15, color: "#475569", marginTop: 4 }}>Record</div>
              </div>
            )}
          </div>
        )}

        {/* Bar chart */}
        <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: isVertical ? 10 : 8 }}>
          {weeks.map((week, i) => {
            const barDelay = BAR_START + i * 6;
            const barPct   = fillBar(frame, barDelay, 28, (week.winRate / maxRate) * 100);
            const isHigh   = week.winRate >= 70;
            const barColor = isHigh ? accentColor : week.winRate >= 55 ? `${accentColor}88` : "#334155";
            const CHART_H  = isVertical ? 280 : 160;

            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                {/* Label above bar */}
                <div style={{ opacity: springFade(frame, fps, barDelay + 18), fontSize: isVertical ? 20 : 14, fontWeight: 700, color: isHigh ? accentColor : "#64748b" }}>
                  {week.winRate}%
                </div>
                {/* Bar container */}
                <div style={{ width: "100%", background: "#0f172a", borderRadius: "6px 6px 0 0", height: CHART_H, display: "flex", alignItems: "flex-end" }}>
                  <div style={{
                    width: "100%",
                    height: `${barPct}%`,
                    background: `linear-gradient(180deg, ${barColor}, ${barColor}77)`,
                    borderRadius: "6px 6px 0 0",
                    boxShadow: isHigh ? `0 0 18px ${accentColor}44` : "none",
                    transition: "none",
                  }} />
                </div>
                {/* Week label */}
                <div style={{ fontSize: isVertical ? 18 : 13, color: "#475569", fontWeight: 600 }}>{week.label}</div>
              </div>
            );
          })}
        </div>
      </AbsoluteFill>

      {/* Bottom progress */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, durationInFrames)}%`, background: accentColor }} />
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
